
import React from 'react';
import { formatCurrency } from '@/utils/formatters';

interface ResultsDisplayProps {
  results: {
    totalInvestment: number;
    totalCapitalAllocated: number;
    totalProfitAfter4Rounds: number;
    totalROI: number;
    totalWithdrawals: number;
    totalBotProfit: number;
  };
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results }) => {
  return (
    <div className="bg-card border-2 border-primary rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl">
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center space-x-2 sm:space-x-3 mb-4">
          <span className="text-2xl sm:text-4xl">🏆</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
            Kết Quả Cuối Cùng
          </h2>
          <span className="text-2xl sm:text-4xl">🏆</span>
        </div>
        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground font-semibold">
          Sau 12 Tháng - 4 Vòng Mô Hình 1-3-6
        </p>
      </div>

      {/* Main Stats Grid - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="text-center p-4 sm:p-6 bg-secondary border border-border rounded-xl sm:rounded-2xl">
          <div className="text-muted-foreground text-xs sm:text-sm mb-2 font-medium">💰 Đầu Tư Ban Đầu</div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
            {formatCurrency(results.totalInvestment)}
          </div>
        </div>

        <div className="text-center p-4 sm:p-6 bg-secondary border border-border rounded-xl sm:rounded-2xl">
          <div className="text-tradebrand-blue text-xs sm:text-sm mb-2 font-medium">🏦 Vốn Được Cấp (Vòng 1)</div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-tradebrand-blue">
            {formatCurrency(results.totalCapitalAllocated)}
          </div>
        </div>

        <div className="text-center p-4 sm:p-6 bg-secondary border border-border rounded-xl sm:rounded-2xl">
          <div className="text-tradebrand-green text-xs sm:text-sm mb-2 font-medium">💎 Tổng lợi nhuận</div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-tradebrand-green">
            {formatCurrency(results.totalProfitAfter4Rounds)}
          </div>
        </div>

        <div className="text-center p-4 sm:p-6 bg-secondary border border-border rounded-xl sm:rounded-2xl">
          <div className="text-primary text-xs sm:text-sm mb-2 font-medium">📈 ROI Tổng Cộng</div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">
            {results.totalROI.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Profit Breakdown Section - Mobile Optimized */}
      <div className="bg-secondary border border-border rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
        <h4 className="font-bold text-primary mb-4 sm:mb-6 text-lg sm:text-xl flex items-center space-x-2">
          <span>🚀</span>
          <span>Tóm Tắt Lợi Nhuận Theo Mô Hình 1-3-6</span>
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-card p-3 sm:p-4 rounded-xl">
            <div className="text-muted-foreground text-xs sm:text-sm mb-1">💵 Lợi nhuận thuần:</div>
            <div className="font-bold text-tradebrand-green text-base sm:text-lg">
              {formatCurrency(results.totalProfitAfter4Rounds - results.totalInvestment)}
            </div>
          </div>
          <div className="bg-card p-3 sm:p-4 rounded-xl">
            <div className="text-muted-foreground text-xs sm:text-sm mb-1">💸 Tổng đã rút:</div>
            <div className="font-bold text-tradebrand-blue text-base sm:text-lg">
              {formatCurrency(results.totalWithdrawals)}
            </div>
          </div>
          <div className="bg-card p-3 sm:p-4 rounded-xl">
            <div className="text-muted-foreground text-xs sm:text-sm mb-1">🤖 Lợi nhuận Bot:</div>
            <div className="font-bold text-primary text-base sm:text-lg">
              {formatCurrency(results.totalBotProfit)}
            </div>
          </div>
          <div className="bg-card p-3 sm:p-4 rounded-xl">
            <div className="text-muted-foreground text-xs sm:text-sm mb-1">📊 ROI hàng tháng:</div>
            <div className="font-bold text-tradebrand-green text-base sm:text-lg">
              {(results.totalROI / 12).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Leverage Info - Mobile Optimized */}
      <div className="text-center mb-4 sm:mb-6">
        <div className="inline-flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 bg-secondary px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl">
          <div className="text-center">
            <div className="text-muted-foreground text-xs sm:text-sm">⚡ Đòn bẩy ban đầu</div>
            <div className="text-lg sm:text-xl font-bold text-tradebrand-blue">
              {(results.totalCapitalAllocated / results.totalInvestment).toFixed(1)}x
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-border"></div>
          <div className="text-center">
            <div className="text-muted-foreground text-xs sm:text-sm">🎯 Hiệu suất tháng</div>
            <div className="text-lg sm:text-xl font-bold text-tradebrand-green">
              {(results.totalROI / 12).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer - Mobile Optimized */}
      <div className="text-center">
        <div className="inline-flex items-center space-x-2 bg-secondary px-4 sm:px-6 py-3 rounded-full">
          <span className="text-primary">⚠️</span>
          <span className="text-xs text-muted-foreground leading-relaxed">
            * Kết quả tính toán dựa trên mô hình 1-3-6 và lợi nhuận ước tính. 
            AI Prop: 5%/tháng × 80% giữ lại | Bot Trading: 10%/tháng tích lũy.
            Kết quả thực tế có thể khác biệt tùy thuộc vào điều kiện thị trường.
          </span>
        </div>
      </div>
    </div>
  );
};
